import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useCallback, useRef } from "react";
import { ImageBackground, Linking } from "react-native";
import { useFragment } from "react-relay";

import {
  Box,
  Pressable,
  Stack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { images } from "@whoosh/design-system/src/theme/images";
import { getFill } from "@whoosh/utilities/src/Cloudinary";

import { CourseCardCourseNote } from "./CourseCardCourseNote";
import { CourseCardSummary } from "./CourseCardSummary";
import { CourseCard_course$key } from "./__generated__/CourseCard_course.graphql";
import { useCourseDisplayName } from "./hooks/useCourseDisplayName";

type Props = {
  courseRef: CourseCard_course$key | null;
  index: number;
};

const imagePlaceHolderMap: Record<number, any> = {
  0: images.coursePlaceholder1,
  1: images.coursePlaceholder2,
  2: images.coursePlaceholder3,
  3: images.coursePlaceholder4,
  4: images.coursePlaceholder5,
};

export const CourseCard = ({ courseRef, index }: Props) => {
  const course = useFragment(CourseCardFragmentString, courseRef);

  const courseDisplayName = useCourseDisplayName((course as any) ?? null);
  const courseDescription = course?.description;
  const courseImage = course?.courseImage?.publicId;
  const courseAddress = course?.facility?.address;

  const accessIndex = index % 5;
  const placeholderImage = imagePlaceHolderMap[accessIndex];

  const navigateToTeeSheetRef = useRef<(() => void) | null>(null);

  const onNavigate = useCallback((fn: () => void) => {
    navigateToTeeSheetRef.current = fn;
  }, []);

  const onCardPress = useCallback(() => {
    navigateToTeeSheetRef.current?.();
  }, []);

  const onAddressPress = useCallback(async () => {
    if (courseAddress) {
      const encodedAddress = encodeURIComponent(courseAddress);
      const googleMapsUrl = `https://maps.google.com/?q=${encodedAddress}`;
      const supported = await Linking.canOpenURL(googleMapsUrl);

      if (supported) {
        await Linking.openURL(googleMapsUrl);
      }
    }
  }, [courseAddress]);

  return (
    <VStack>
      <Pressable onPress={onCardPress} accessibilityLabel={courseDisplayName}>
        <Stack
          width={{ base: "100%", sm: 760 }}
          maxWidth={{ base: "100%", sm: 760 }}
          flexDirection={{ base: "column", sm: "row" }}
          gap={{ base: 2, sm: 6 }}
          overflow="hidden"
        >
          <Box
            borderRadius="2"
            height={{ base: 215, sm: 180 }}
            width={{ base: "100%", sm: 288 }}
            backgroundColor="surface-secondary"
            overflow="hidden"
          >
            <ImageBackground
              style={{ height: "100%", width: "100%" }}
              source={
                courseImage
                  ? { uri: getFill(courseImage, 600, 338)?.toURL() }
                  : placeholderImage
              }
              resizeMode="cover"
            />
          </Box>
          <VStack
            justifyContent="space-between"
            overflow="hidden"
            flex={1}
            paddingVertical={2}
            gap={3}
          >
            <VStack flex={1} gap={2} overflow={"hidden"}>
              <Text variant="mainHeading">{courseDisplayName}</Text>
              {courseDescription ? (
                <Text
                  variant="mainSmallMedium"
                  color="text-secondary"
                  numberOfLines={2}
                >
                  {courseDescription}
                </Text>
              ) : null}
              {courseAddress ? (
                <Pressable
                  _hover={{ borderBottomWidth: 1 }}
                  flexGrow={0}
                  flexShrink={1}
                  alignSelf="flex-start"
                  onPress={onAddressPress}
                  accessibilityRole="link"
                  accessibilityLabel={t`Open ${courseAddress} in Maps`}
                >
                  <Text variant="mainSmallMedium">{courseAddress}</Text>
                </Pressable>
              ) : null}
            </VStack>
            <CourseCardSummary
              courseRef={course ?? null}
              onNavigate={onNavigate}
            />
          </VStack>
        </Stack>
      </Pressable>
      <CourseCardCourseNote courseRef={course ?? null} />
    </VStack>
  );
};

const CourseCardFragmentString = graphql`
  fragment CourseCard_course on Course {
    ...useCourseDisplayName_course
    ...CourseCardSummary_course
    ...CourseCardCourseNote_course
    description
    courseImage {
      # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
      publicId: renderingKey
    }
    facility {
      address
    }
  }
`;
